// The exact double values for the max and min scores possible in each range.
const MIN_PASSING_SCORE = 0.90000000000000002220446049250313080847263336181640625;
const MAX_AVERAGE_SCORE = 0.899999999999999911182158029987476766109466552734375;
const MIN_AVERAGE_SCORE = 0.5;
const MAX_FAILING_SCORE = 0.499999999999999944488848768742172978818416595458984375;

export type TPerformanceMeasurementConfig = {
  p10: number;
  median: number;
};

export const CLS_PERFORMANCE_CONFIG: TPerformanceMeasurementConfig = {
  p10: 0.1,
  median: 0.25,
};

export const FCP_PERFORMANCE_CONFIG: TPerformanceMeasurementConfig = {
  p10: 934,
  median: 1600,
};

export const LCP_PERFORMANCE_CONFIG: TPerformanceMeasurementConfig = {
  p10: 1200,
  median: 2400,
};

export const TBT_PERFORMANCE_CONFIG: TPerformanceMeasurementConfig = {
  p10: 150,
  median: 350,
};

export const TTI_PERFORMANCE_CONFIG: TPerformanceMeasurementConfig = {
  p10: 2468,
  median: 4500,
};

const erf = (i: number) => {
  // erf(-x) = -erf(x);
  const sign = Math.sign(i);
  const x = Math.abs(i);

  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const t = 1 / (1 + p * x);
  const y = t * (a1 + t * (a2 + t * (a3 + t * (a4 + t * a5))));
  return sign * (1 - y * Math.exp(-x * x));
};

const getLogNormalScore = ({ median, p10 }: TPerformanceMeasurementConfig, value: number) => {
  // Required for the log-normal distribution.
  if (median <= 0) throw new Error('median must be greater than zero');
  if (p10 <= 0) throw new Error('p10 must be greater than zero');
  // Not strictly required, but if p10 > median, it flips around and becomes the p90 point.
  if (p10 >= median) throw new Error('p10 must be less than the median');

  // Non-positive values aren't in the distribution, so always 1.
  if (value <= 0) return 1;

  // Closest double to `erfc-1(1/5)`.
  const INVERSE_ERFC_ONE_FIFTH = 0.9061938024368232;

  // Shape (σ) is `|log(p10/median) / (sqrt(2)*erfc^-1(1/5))|` and
  // standardizedX is `1/2 erfc(log(value/median) / (sqrt(2)*σ))`, so simplify a bit.
  const xRatio = Math.max(Number.MIN_VALUE, value / median); // value and median are > 0, so is ratio.
  const xLogRatio = Math.log(xRatio);
  const p10Ratio = Math.max(Number.MIN_VALUE, p10 / median); // p10 and median are > 0, so is ratio.
  const p10LogRatio = -Math.log(p10Ratio); // negate to keep σ positive.
  const standardizedX = (xLogRatio * INVERSE_ERFC_ONE_FIFTH) / p10LogRatio;
  const complementaryPercentile = (1 - erf(standardizedX)) / 2;

  // Clamp to avoid floating-point out-of-bounds issues and keep score in expected range.
  let score;
  if (value <= p10) {
    // Passing. Clamp to [0.9, 1].
    score = Math.max(MIN_PASSING_SCORE, Math.min(1, complementaryPercentile));
  } else if (value <= median) {
    // Average. Clamp to [0.5, 0.9).
    score = Math.max(MIN_AVERAGE_SCORE, Math.min(MAX_AVERAGE_SCORE, complementaryPercentile));
  } else {
    // Failing. Clamp to [0, 0.5).
    score = Math.max(0, Math.min(MAX_FAILING_SCORE, complementaryPercentile));
  }
  return score;
};

export const computeLogNormalScore = (controlPoints: TPerformanceMeasurementConfig, value: number) => {
  let percentile = getLogNormalScore(controlPoints, value);
  // Add a boost to scores of 90+, linearly ramping from 0 at 0.9 to half a
  // point (0.005) at 1. Expands scores in (0.9, 1] to (0.9, 1.005], so more top
  // scores will be a perfect 1 after the two-digit `Math.floor()` rounding below.
  if (percentile > 0.9) {
    // getLogNormalScore ensures `percentile` can't exceed 1.
    percentile += 0.05 * (percentile - 0.9);
  }
  return Math.floor(percentile * 100) / 100;
};
